import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
  styled,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Container,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import ShopIcon from '@mui/icons-material/Shop';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import UsersIcon from '@mui/icons-material/People';
import BookOpenIcon from '@mui/icons-material/MenuBook';
import PackageIcon from '@mui/icons-material/Inventory';
import ListChecksIcon from '@mui/icons-material/ChecklistRtl';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckIcon from '@mui/icons-material/Check';
import SparklesIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CrownIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';
import { useRef, useEffect, useState } from 'react';
import Silk from '../../components/Silk';
import { translations } from '../../translations';

// theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#744439', // um pouco mais quente para o gradiente
      paper: '#FFFFFF',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#E0E0E0',
    },
    primary: {
      main: '#F3A8B6', // rosa do mock
    },
  },
  typography: {
    fontFamily: '"Lora", "serif"',
    h1: {
      fontFamily: '"Montserrat", "sans-serif"',
      fontWeight: 800,
      fontSize: '5.2rem', // maior como no banner
      lineHeight: 1.05,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    h6: {
      fontFamily: '"Inter", "sans-serif"',
    },
    body1: {
      fontFamily: '"Inter", "sans-serif"',
    },
    button: {
      fontFamily: '"Inter", "sans-serif"',
      textTransform: 'none',
      fontWeight: 600,
    },
  },
});

// Badge de loja mais próximo do layout
const StoreBadge = styled(Button)(() => ({
  backgroundColor: 'rgba(0,0,0,0.55)',
  color: '#fff',
  borderRadius: 12,
  padding: '10px 16px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.08)',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.65)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.12)',
  },
  fontWeight: 700,
  textTransform: 'none',
  minWidth: 160,
  transition: 'all 0.3s ease',
  animation: 'subtle-pulse 3s ease-in-out infinite',
  '@keyframes subtle-pulse': {
    '0%, 100%': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.08)',
    },
    '50%': {
      boxShadow: '0 4px 20px rgba(255,255,255,0.15), inset 0 0 0 1px rgba(255,255,255,0.15)',
    },
  },
})) as any;

const App = () => {
  const navigate = useNavigate();
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>('free');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);

  const freeRef = useRef<HTMLDivElement>(null);
  const basicRef = useRef<HTMLDivElement>(null);
  const proRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const plansRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);

  const t = translations['pt'];

  useEffect(() => {
    if (window.clarity) {
      window.clarity('set', 'pageType', 'landing');
    }
  }, []);

  useEffect(() => {
    const hasSeenVideo = localStorage.getItem('hostly_intro_video_seen');
    if (!hasSeenVideo) {
      setShowVideoOverlay(true);
    }
  }, []);

  const handleCloseVideo = () => {
    localStorage.setItem('hostly_intro_video_seen', 'true');
    setShowVideoOverlay(false);
  };

  // Detectar scroll para aplicar blur no nav
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.8) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver para abrir/fechar accordions ao rolar
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0.1,
    };

    const callback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('data-accordion-id');
          if (id) setExpandedAccordion(id);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    if (freeRef.current) observer.observe(freeRef.current);
    if (basicRef.current) observer.observe(basicRef.current);
    if (proRef.current) observer.observe(proRef.current);

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const headerOffset = 100; // Altura do header fixo
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const containerStyles = {
    position: 'relative' as const,
    minHeight: '100vh',
    overflow: 'hidden' as const
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{
        '@import url("https://fonts.googleapis.com/css2?family=Lora:wght@700&family=Inter:wght@400;600&family=Montserrat:wght@600;800&display=swap");': {},
        '@keyframes float': {
          '0%, 100%': {
            transform: 'translateY(0px)',
          },
          '50%': {
            transform: 'translateY(-15px)',
          },
        },
      }} />

      {showVideoOverlay && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <IconButton
            onClick={handleCloseVideo}
            sx={{
              position: 'absolute',
              top: { xs: 16, md: 32 },
              right: { xs: 16, md: 32 },
              color: '#fff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
              zIndex: 10000,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{
            width: isMobile ? '100%' : '90%',
            maxWidth: isMobile ? '100%' : '500px',
            height: isMobile ? '100%' : 'auto',
            aspectRatio: isMobile ? 'auto' : '9/16',
            maxHeight: isMobile ? '100%' : '90vh',
          }}>
            <iframe
              src="https://www.youtube.com/embed/1WaGAMETrNw?autoplay=1&rel=0&modestbranding=1"
              title="Hostly Introduction"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: isMobile ? 0 : '16px',
              }}
            />
          </Box>

          <Typography
            onClick={handleCloseVideo}
            sx={{
              position: 'absolute',
              bottom: { xs: 32, md: 48 },
              color: '#F5E8E4',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
              opacity: 0.8,
              '&:hover': { opacity: 1 },
            }}
          >
            Pular vídeo
          </Typography>
        </Box>
      )}

      <Box sx={containerStyles}>
        {/* Background image - fixo para todas as seções */}
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          backgroundImage: 'url(/background.png)',
          backgroundSize: { xs: 'auto 100%', md: 'cover' },
          backgroundPosition: { xs: 'center center', md: 'center' },
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
        }} />

        {/* Background com Silk quase transparente */}
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, opacity: 0.15 }}>
          <Silk
            speed={5}
            scale={1}
            color="#744439"
            color2="#915b47"
            noiseIntensity={1.5}
            rotation={0}
          />
        </Box>

        {/* Header - fixed no topo com backdrop blur apenas quando rolar */}
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: { xs: 2, md: 4 },
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          backgroundColor: 'transparent',
          transition: 'backdrop-filter 0.3s ease',
          pointerEvents: 'auto',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box component="img" src="/pranchetaWhite.png" alt="Hostly" sx={{ height: { xs: 45, md: 55 }, cursor: 'pointer' }} onClick={() => scrollToSection(heroRef)} />
          </Box>

          {/* Desktop Nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, color: 'text.primary' }}>
            <Link onClick={() => scrollToSection(heroRef)} color="inherit" underline="none" sx={{ cursor: 'pointer' }}><Typography variant="body1">{t.nav.home}</Typography></Link>
            <Typography variant="body1" sx={{ opacity: 0.6 }}>|</Typography>
            <Link onClick={() => scrollToSection(featuresRef)} color="inherit" underline="none" sx={{ cursor: 'pointer' }}><Typography variant="body1">{t.nav.about}</Typography></Link>
            <Typography variant="body1" sx={{ opacity: 0.6 }}>|</Typography>
            <Link onClick={() => scrollToSection(plansRef)} color="inherit" underline="none" sx={{ cursor: 'pointer' }}><Typography variant="body1">{t.nav.plans}</Typography></Link>
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            sx={{ display: { xs: 'block', md: 'none' }, color: 'text.primary' }}
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{
              sx: {
                bgcolor: 'rgba(67, 45, 55, 0.98)',
                backdropFilter: 'blur(10px)',
                color: 'text.primary',
                width: '75vw',
                maxWidth: 300,
                borderLeft: '1px solid rgba(255, 182, 193, 0.2)',
              }
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 182, 193, 0.1)' }}>
              <Box component="img" src="/pranchetaWhite.png" alt="Hostly" sx={{ height: 40 }} />
              <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <List sx={{ pt: 4 }}>
              <ListItem
                onClick={() => scrollToSection(heroRef)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  '&:hover': { bgcolor: 'rgba(255, 182, 193, 0.1)' },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemText
                  primary={t.nav.home}
                  primaryTypographyProps={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}
                />
              </ListItem>
              <ListItem
                onClick={() => scrollToSection(featuresRef)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  '&:hover': { bgcolor: 'rgba(255, 182, 193, 0.1)' },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemText
                  primary={t.nav.about}
                  primaryTypographyProps={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}
                />
              </ListItem>
              <ListItem
                onClick={() => scrollToSection(plansRef)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  '&:hover': { bgcolor: 'rgba(255, 182, 193, 0.1)' },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemText
                  primary={t.nav.plans}
                  primaryTypographyProps={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}
                />
              </ListItem>
            </List>
          </Drawer>
        </Box>

        {/* Container para decorações e conteúdo */}
        <Box sx={{ position: 'relative', zIndex: 2, p: { xs: 2, md: 4 } }}>
          {/* DECORAÇÕES */}
          <Box
            component="img"
            src="/hostly-02.png"
            alt="decoração"
            sx={{
              position: 'absolute',
              top: { xs: '-6%', md: '-10%' },
              left: { xs: '30%', md: '38%' },
              width: { xs: 80, md: 140 },
              transform: 'rotate(12deg)',
              zIndex: 1
            }}
          />
          <Box
            component="img"
            src="/hostly-02.png"
            alt="decoração"
            sx={{
              position: 'absolute',
              top: { xs: '6%', md: '8%' },
              right: { xs: '8%', md: '14%' },
              width: { xs: 90, md: 160 },
              transform: 'rotate(-8deg)',
              zIndex: 1
            }}
          />
          <Box
            component="img"
            src="/hostly-02.png"
            alt="decoração"
            sx={{
              position: 'absolute',
              bottom: { xs: '6%', md: '8%' },
              right: { xs: '2%', md: '4%' },
              width: { xs: 120, md: 220 },
              transform: 'rotate(-22deg)',
              zIndex: 1
            }}
          />

          {/* Conteúdo Principal - SEÇÃO HERO */}
          <Grid
            ref={heroRef}
            container
            spacing={4}
            alignItems="center"
            sx={{ minHeight: { xs: '100vh', md: '100vh' }, pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 }, position: 'relative' }}
          >
            {/* ESQUERDA: centralizar texto e botões */}
            <Grid
              sx={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: { xs: '40px', sm: '56px', md: '72px', lg: '90px' },
                    fontWeight: 800,
                    color: '#d8a1a4',
                    letterSpacing: { xs: '2px', md: '3px' },
                    lineHeight: 0.9,
                    fontFamily: '"Montserrat", sans-serif',
                    mb: 0,
                  }}
                >
                  {t.hero.title1}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '28px', sm: '38px', md: '48px', lg: '60px' },
                    fontWeight: 700,
                    color: '#d8a1a4',
                    letterSpacing: { xs: '2px', md: '3px' },
                    mt: { xs: '8px', md: '10px' },
                    fontFamily: '"Montserrat", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  {t.hero.title2}
                </Typography>
              </Box>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: 1,
                  mt: { xs: 2, md: 3 },
                  maxWidth: 480,
                  letterSpacing: 0.3,
                  textAlign: 'center',
                  fontSize: { xs: '0.95rem', md: '1.25rem' },
                  px: { xs: 2, md: 0 }
                }}
              >
                {t.hero.subtitle}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, justifyContent: 'center', flexWrap: 'wrap', px: { xs: 2, md: 0 } }}>
                  <StoreBadge
                    component="a"
                    href="https://apps.apple.com/br/app/hostly/id6755368891"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<AppleIcon />}
                    sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, minWidth: { xs: 140, md: 160 } }}
                  >
                    App Store
                  </StoreBadge>
                  <StoreBadge
                    component="a"
                    startIcon={<ShopIcon />}
                    href="https://play.google.com/store/apps/details?id=br.com.hostly.app&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, minWidth: { xs: 140, md: 160 } }}
                  >
                    {t.hero.googlePlay}
                  </StoreBadge>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: { xs: '0.75rem', md: '0.85rem' },
                    fontWeight: 400,
                    letterSpacing: 0.3,
                    textAlign: 'center'
                  }}
                >
                  Clique para baixar
                </Typography>
              </Box>
            </Grid>

            {/* DIREITA: smartphone */}
            <Grid
              sx={{ display: 'flex', justifyContent: 'center' }}
              size={{
                xs: 12,
                md: 6
              }}>
              {/* Imagem do login screen */}
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                {/* Layer image atrás do celular */}
                <Box
                  component="img"
                  src="/layer.png"
                  alt="layer"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '250%',
                    height: 'auto',
                    zIndex: -1,
                  }}
                />

                <Box
                  component="img"
                  src="/heroImage.png"
                  alt="Hostly App"
                  sx={{
                    width: { xs: '240px', sm: '280px', md: '320px', lg: '340px' },
                    height: 'auto',
                    maxWidth: '100%',
                    display: 'block',
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Setinha flutuante indicando scroll */}
          <Box
            onClick={() => scrollToSection(featuresRef)}
            sx={{
              position: 'absolute',
              bottom: { xs: 20, md: 30 },
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
              animation: 'float 2s ease-in-out infinite',
              opacity: 0.6,
              transition: 'opacity 0.3s ease',
              '&:hover': {
                opacity: 1,
              },
              zIndex: 10,
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: { xs: '3rem', md: '4rem' },
                color: '#F3A8B6',
                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
              }}
            />
          </Box>
        </Box>

        {/* SEGUNDA SEÇÃO - App Mockup + Texto */}
        <Box ref={featuresRef} sx={{ position: 'relative', zIndex: 2, minHeight: { xs: '100vh', md: '100vh' }, display: 'flex', alignItems: 'center', p: { xs: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
          <Grid container spacing={{ xs: 4, md: 12 }} alignItems="center" justifyContent="center" sx={{ maxWidth: '1400px', mx: 'auto' }}>

            {/* DIREITA: Blocos de Texto - No mobile vem primeiro */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '600px' }}>

                {/* Logo hostly */}
                <Box sx={{ alignSelf: 'flex-start' }}>
                  <Box component="img" src="/pranchetaWhite.png" alt="Hostly" sx={{ height: { xs: 45, md: 55 }, display: 'block' }} />
                </Box>

                {/* Bloco 1 com borda */}
                <Box sx={{
                  border: '2px solid #D96C9B',
                  borderRadius: '12px',
                  p: { xs: 2, md: 3 },
                }}>
                  <Typography sx={{
                    fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '20px' },
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    lineHeight: 1.6,
                    color: '#F5E8E4',
                  }}>
                    {t.features.feature1}
                  </Typography>
                </Box>

                {/* Bloco 2 sem borda */}
                <Typography sx={{
                  fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '20px' },
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  lineHeight: 1.6,
                  color: '#F5E8E4',
                }}>
                  {t.features.feature2}
                </Typography>

                {/* Bloco 3 com borda */}
                <Box sx={{
                  border: '2px solid #D96C9B',
                  borderRadius: '12px',
                  p: { xs: 2, md: 3 },
                }}>
                  <Typography sx={{
                    fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '20px' },
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    lineHeight: 1.6,
                    color: '#F5E8E4',
                  }}>
                    {t.features.feature3}
                  </Typography>
                </Box>

                {/* Bloco 4 sem borda */}
                <Typography sx={{
                  fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '20px' },
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  lineHeight: 1.6,
                  color: '#F5E8E4',
                }}>
                  {t.features.feature4}
                </Typography>

              </Box>
            </Grid>

            {/* ESQUERDA: Mockup do App - Oculto no mobile */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', order: { xs: 2, md: 1 } }}>
              <Box
                component="img"
                src="/loginScreen.png"
                alt="App Dashboard"
                sx={{
                  width: { xs: '280px', md: '340px' },
                  height: 'auto',
                  maxWidth: '100%',
                }}
              />
            </Grid>

          </Grid>
        </Box>

        {/* TERCEIRA SEÇÃO - Planos */}
        <Box ref={plansRef} sx={{ position: 'relative', zIndex: 2, minHeight: { xs: 'auto', md: '100vh' }, display: 'flex', alignItems: 'center', p: { xs: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
          <Box sx={{ maxWidth: '1200px', mx: 'auto', width: '100%' }}>

            {/* Título */}
            <Box sx={{
              background: '#D698A0',
              color: '#513232',
              fontWeight: 700,
              textTransform: 'uppercase',
              py: { xs: 1.5, md: 2 },
              px: { xs: 2, md: 4 },
              borderRadius: '8px',
              boxShadow: 3,
              textAlign: 'center',
              mb: 3,
            }}>
              <Typography sx={{ fontSize: { xs: '12px', sm: '14px', md: '16px' }, fontWeight: 700 }}>
                {t.plans.title}
              </Typography>
            </Box>

            {/* Toggle Mensal/Anual */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 4, md: 6 } }}>
              <ToggleButtonGroup
                value={billingPeriod}
                exclusive
                onChange={(_, newPeriod) => newPeriod && setBillingPeriod(newPeriod)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '12px',
                  border: '2px solid #D96C9B',
                }}
              >
                <ToggleButton
                  value="monthly"
                  sx={{
                    px: 3,
                    py: 1,
                    color: '#F5E8E4',
                    fontWeight: 600,
                    border: 'none',
                    '&.Mui-selected': {
                      backgroundColor: '#D96C9B',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#C85A87',
                      },
                    },
                  }}
                >
                  Mensal
                </ToggleButton>
                <ToggleButton
                  value="annual"
                  sx={{
                    px: 3,
                    py: 1,
                    color: '#F5E8E4',
                    fontWeight: 600,
                    border: 'none',
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: '#D96C9B',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#C85A87',
                      },
                    },
                  }}
                >
                  Anual
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 700,
                      px: 1,
                      py: 0.5,
                      borderRadius: '6px',
                    }}
                  >
                    %
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Mobile: Accordions */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>

              {/* FREE Accordion */}
              <Accordion
                ref={freeRef}
                data-accordion-id="free"
                expanded={expandedAccordion === 'free'}
                onChange={(e: React.SyntheticEvent, isExpanded: boolean) => {
                  e.stopPropagation();
                  setExpandedAccordion(isExpanded ? 'free' : false);
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(8px)',
                  border: '2px solid #D96C9B',
                  borderRadius: '24px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#F5E8E4' }} />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SparklesIcon sx={{ fontSize: 24, color: '#D96C9B' }} />
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, textTransform: 'uppercase', color: '#F5E8E4' }}>
                        Free
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                      Grátis
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#F5E8E4', mb: 0.5 }}>
                      R$ 0
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#F5E8E4', opacity: 0.7 }}>
                      Grátis para sempre
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, fontSize: '12px', color: '#F5E8E4' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>1 evento por mês</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 fotos por evento</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Vídeos por evento</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UsersIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>2 grupos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Envio de convites</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BookOpenIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload de vídeos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Calculadora de porções</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PackageIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>30 itens</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>3 categorias personalizadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico de uso</Typography>
                      </Box>
                    </Box>

                    {/* Organização */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ListChecksIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Tarefas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Listas de compras</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Wishlist</Typography>
                      </Box>
                    </Box>

                    {/* Recursos Premium */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ShieldIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Recursos Premium</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Suporte prioritário</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/pricing')}
                    sx={{
                      mt: 3,
                      backgroundColor: '#D96C9B',
                      color: '#FFF',
                      fontWeight: 700,
                      borderRadius: '12px',
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#C55B8A'
                      }
                    }}
                  >
                    {t.plans.viewDetails}
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* BASIC Accordion */}
              <Accordion
                ref={basicRef}
                data-accordion-id="basic"
                expanded={expandedAccordion === 'basic'}
                onChange={(e: React.SyntheticEvent, isExpanded: boolean) => {
                  e.stopPropagation();
                  setExpandedAccordion(isExpanded ? 'basic' : false);
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(8px)',
                  border: '2px solid #D96C9B',
                  borderRadius: '24px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#F5E8E4' }} />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                    <SparklesIcon sx={{ fontSize: 24, color: '#D96C9B' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, textTransform: 'uppercase', color: '#F5E8E4' }}>
                        Basic
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {billingPeriod === 'annual' && (
                        <Typography sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#F5E8E4',
                          textDecoration: 'line-through',
                          opacity: 0.6
                        }}>
                          DE: R$ 238
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#D96C9B' }}>
                        {billingPeriod === 'monthly' ? 'R$ 19,90/mês' : (
                          <span>POR R$ 199,90/ano</span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, fontSize: '12px', color: '#F5E8E4' }}>
                    {/* Eventos */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>10 eventos por mês</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico ilimitado</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>50 fotos + vídeos</Typography>
                      </Box>
                    </Box>

                    {/* Convidados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UsersIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>50 convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>10 grupos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Envio de convites</Typography>
                      </Box>
                    </Box>

                    {/* Receitas */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BookOpenIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>30 receitas no caderno</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload de vídeos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Calculadora de porções</Typography>
                      </Box>
                    </Box>

                    {/* Acervo */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PackageIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>100 itens cadastrados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>15 categorias personalizadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico de uso</Typography>
                      </Box>
                    </Box>

                    {/* Organização */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ListChecksIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Tarefas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 listas de compras ativas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>30 itens na wishlist</Typography>
                      </Box>
                    </Box>

                    {/* Recursos Premium */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ShieldIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Recursos Premium</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Suporte prioritário</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/pricing')}
                    sx={{
                      mt: 3,
                      backgroundColor: '#D96C9B',
                      color: '#FFF',
                      fontWeight: 700,
                      borderRadius: '12px',
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#C55B8A'
                      }
                    }}
                  >
                    {t.plans.subscribeNow}
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* PRO Accordion */}
              <Accordion
                ref={proRef}
                data-accordion-id="pro"
                expanded={expandedAccordion === 'pro'}
                onChange={(_: React.SyntheticEvent, isExpanded: boolean) => {
                  if (isExpanded) {
                    setExpandedAccordion(isExpanded ? 'pro' : false);
                  }
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(8px)',
                  border: '2px solid #D96C9B',
                  borderRadius: '24px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#F5E8E4' }} />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                    <CrownIcon sx={{ fontSize: 24, color: '#FFD700' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, textTransform: 'uppercase', color: '#F5E8E4' }}>
                        Pro
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {billingPeriod === 'annual' && (
                        <Typography sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#F5E8E4',
                          textDecoration: 'line-through',
                          opacity: 0.6
                        }}>
                          DE: R$ 310
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#D96C9B' }}>
                        {billingPeriod === 'monthly' ? 'R$ 25,90/mês' : (
                          <span>POR R$ 259,90/ano</span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, fontSize: '12px', color: '#F5E8E4' }}>
                    {/* Eventos */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Eventos ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico ilimitado</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload ilimitado</Typography>
                      </Box>
                    </Box>

                    {/* Gestão de Convidados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UsersIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Convidados ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Grupos ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Envio de convites</Typography>
                      </Box>
                    </Box>

                    {/* Receitas */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BookOpenIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Receitas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload de vídeos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Calculadora de porções</Typography>
                      </Box>
                    </Box>

                    {/* Acervo */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PackageIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Itens ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Categorias ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico de uso</Typography>
                      </Box>
                    </Box>

                    {/* Organização */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ListChecksIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Tarefas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Listas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Wishlist ilimitada</Typography>
                      </Box>
                    </Box>

                    {/* Recursos Premium */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ShieldIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Recursos Premium</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Suporte prioritário</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/pricing')}
                    sx={{
                      mt: 3,
                      backgroundColor: '#FFD700',
                      color: '#513232',
                      fontWeight: 700,
                      borderRadius: '12px',
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#FFC700'
                      }
                    }}
                  >
                    {t.plans.subscribePro}
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Desktop: Grid de Cards */}
            <Grid container spacing={{ xs: 3, md: 4 }} sx={{ display: { xs: 'none', md: 'flex' } }}>

              {/* Card FREE */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{
                  border: '2px solid #D96C9B',
                  borderRadius: '24px',
                  p: 3,
                  height: '100%',
                  minHeight: '700px',
                  backdropFilter: 'blur(8px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
                  onClick={() => navigate('/pricing')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <SparklesIcon sx={{ fontSize: 28, color: '#D96C9B' }} />
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, textTransform: 'uppercase', color: '#F5E8E4', textAlign: 'center' }}>
                      Free
                    </Typography>
                  </Box>

                  {/* Preço */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#F5E8E4' }}>
                      R$ 0
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#F5E8E4', opacity: 0.7 }}>
                      {t.plans.freeForever}
                    </Typography>
                  </Box>

                  {/* Features FREE */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, fontSize: '12px', color: '#F5E8E4', flex: 1 }}>
                    {/* Eventos */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>1 evento por mês</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 fotos por evento</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Vídeos</Typography>
                      </Box>
                    </Box>

                    {/* Gestão de Convidados */}
                    {/* Gestão de Convidados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UsersIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>2 grupos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Envio de convites</Typography>
                      </Box>
                    </Box>

                    {/* Receitas */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BookOpenIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload de vídeos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Calculadora de porções</Typography>
                      </Box>
                    </Box>

                    {/* Acervo */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PackageIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>30 itens</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>3 categorias</Typography>
                      </Box>
                    </Box>

                    {/* Organização */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ListChecksIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Tarefas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Listas de compras</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Wishlist</Typography>
                      </Box>
                    </Box>

                    {/* Recursos Premium */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ShieldIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Recursos Premium</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Suporte prioritário</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Card BASIC */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{
                  border: '2px solid #D96C9B',
                  borderRadius: '24px',
                  p: 3,
                  height: '100%',
                  minHeight: '700px',
                  backdropFilter: 'blur(8px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
                  onClick={() => navigate('/pricing')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <SparklesIcon sx={{ fontSize: 28, color: '#D96C9B' }} />
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, textTransform: 'uppercase', color: '#F5E8E4', textAlign: 'center' }}>
                      Basic
                    </Typography>
                  </Box>

                  {/* Preço */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {billingPeriod === 'annual' && (
                      <Typography sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#F5E8E4',
                        textDecoration: 'line-through',
                        opacity: 0.6,
                        mb: 0.5
                      }}>
                        DE: R$ 238
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {billingPeriod === 'annual' && (
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#F5E8E4', opacity: 0.8 }}>
                          POR
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#F5E8E4' }}>
                        {billingPeriod === 'monthly' ? 'R$ 19,90' : 'R$ 199,90'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: '#F5E8E4', opacity: 0.7 }}>
                      {billingPeriod === 'monthly' ? '/mês' : '/ano'}
                    </Typography>
                  </Box>

                  {/* Features BASIC - dados do pricingPage */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, fontSize: '12px', color: '#F5E8E4', flex: 1 }}>
                    {/* Eventos */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>10 eventos por mês</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico ilimitado</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>50 fotos + vídeos</Typography>
                      </Box>
                    </Box>

                    {/* Gestão de Convidados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UsersIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>50 convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>10 grupos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Envio de convites</Typography>
                      </Box>
                    </Box>

                    {/* Receitas */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BookOpenIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>30 receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload de vídeos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Calculadora de porções</Typography>
                      </Box>
                    </Box>

                    {/* Acervo */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PackageIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>100 itens</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>15 categorias personalizadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico de uso</Typography>
                      </Box>
                    </Box>

                    {/* Organização */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ListChecksIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Tarefas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>5 listas de compras ativas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>30 itens na wishlist</Typography>
                      </Box>
                    </Box>

                    {/* Recursos Premium */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ShieldIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Recursos Premium</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                        <Typography>Suporte prioritário</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Card PRO */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{
                  border: '2px solid #D96C9B',
                  borderRadius: '24px',
                  p: 3,
                  height: '100%',
                  minHeight: '700px',
                  backdropFilter: 'blur(8px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
                  onClick={() => navigate('/pricing')}
                >
                  {/* Estrela no canto */}
                  <Box
                    component="img"
                    src="/hostly-02.png"
                    alt="star"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      transform: 'translate(25%, -25%)',
                      width: 112,
                      height: 112,
                      objectFit: 'contain',
                      opacity: 0.8,
                    }}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
                    <CrownIcon sx={{ fontSize: 32, color: '#FFD700' }} />
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, textTransform: 'uppercase', color: '#F5E8E4' }}>
                      Pro
                    </Typography>
                  </Box>

                  {/* Preço */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {billingPeriod === 'annual' && (
                      <Typography sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#F5E8E4',
                        textDecoration: 'line-through',
                        opacity: 0.6,
                        mb: 0.5
                      }}>
                        DE: R$ 310
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {billingPeriod === 'annual' && (
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#F5E8E4', opacity: 0.8 }}>
                          POR
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#F5E8E4' }}>
                        {billingPeriod === 'monthly' ? 'R$ 25,90' : 'R$ 259,90'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: '#F5E8E4', opacity: 0.7 }}>
                      {billingPeriod === 'monthly' ? '/mês' : '/ano'}
                    </Typography>
                  </Box>

                  {/* Features PRO - dados do pricingPage */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, fontSize: '12px', color: '#F5E8E4', flex: 1 }}>
                    {/* Eventos */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Eventos ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico ilimitado</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload ilimitado</Typography>
                      </Box>
                    </Box>

                    {/* Gestão de Convidados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UsersIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Convidados ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Grupos ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Envio de convites</Typography>
                      </Box>
                    </Box>

                    {/* Receitas */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BookOpenIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Receitas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Upload de vídeos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Calculadora de porções</Typography>
                      </Box>
                    </Box>

                    {/* Acervo */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PackageIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Itens ilimitados</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Categorias ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Histórico de uso</Typography>
                      </Box>
                    </Box>

                    {/* Organização */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ListChecksIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Tarefas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Listas ilimitadas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Wishlist ilimitada</Typography>
                      </Box>
                    </Box>

                    {/* Recursos Premium */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ShieldIcon sx={{ fontSize: 18, color: '#D96C9B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Recursos Premium</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                        <Typography>Suporte prioritário</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>

            </Grid>
          </Box>
        </Box>

        {/* SEÇÃO SOBRE A CRIADORA */}
        <Box
          ref={aboutRef}
          sx={{
            position: 'relative',
            zIndex: 2,
            minHeight: { xs: 'auto', md: '80vh' },
            display: 'flex',
            alignItems: 'center',
            p: { xs: 3, md: 4 },
            py: { xs: 8, md: 12 }
          }}>
          <Container maxWidth="xl">
            {/* MOBILE: Layout vertical com cards */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 3 }}>
              {/* Card de texto - Mobile */}
              <Box sx={{
                width: '100%',
                backgroundColor: 'rgba(243, 168, 182, 0.15)',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(243, 168, 182, 0.3)',
                borderRadius: '24px',
                p: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}>
                <Typography sx={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#F5E8E4',
                  fontWeight: 500,
                  textAlign: 'left',
                }}>
                  "Receber amigos, familiares ou convidados especiais em casa é um prazer, mas também pode ser uma tarefa desafiadora: lista de compras, organização da mesa, convites, cronograma… Muitas vezes, quem recebe se perde nos detalhes e esquece de aproveitar o momento."
                </Typography>
              </Box>

              {/* Imagem - Mobile */}
              <Box
                component="img"
                src="/aboutProfilePhoto.png"
                alt="Criadora do Hostly"
                sx={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  borderRadius: '24px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  border: '3px solid rgba(243, 168, 182, 0.2)',
                }}
              />

              {/* Card do nome - Mobile */}
              <Box sx={{
                width: '100%',
                backgroundColor: 'rgba(243, 168, 182, 0.15)',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(243, 168, 182, 0.3)',
                borderRadius: '24px',
                p: 2.5,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                textAlign: 'center',
              }}>
                <Typography sx={{
                  fontSize: '13px',
                  fontWeight: 300,
                  color: '#F5E8E4',
                  mb: 0.5,
                  fontFamily: '"Inter", "sans-serif"',
                }}>
                  por
                </Typography>
                <Typography sx={{
                  fontSize: '20px',
                  fontWeight: 800,
                  color: '#F5E8E4',
                  textTransform: 'uppercase',
                  fontFamily: '"Montserrat", "sans-serif"',
                  letterSpacing: '1px',
                }}>
                  Rebecca Linhares
                </Typography>
              </Box>
            </Box>

            {/* DESKTOP: Layout com cards sobrepostos */}
            <Box sx={{
              position: 'relative',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              width: '100%',
            }}>
              {/* Card com texto - Desktop */}
              <Box sx={{
                position: 'absolute',
                top: -100,
                right: 40,
                width: { sm: '400px', md: '450px' },
                backgroundColor: 'rgba(243, 168, 182, 0.15)',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(243, 168, 182, 0.3)',
                borderRadius: '24px',
                p: 3.5,
                zIndex: 10,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}>
                <Typography sx={{
                  fontSize: '16px',
                  lineHeight: 1.7,
                  color: '#F5E8E4',
                  fontWeight: 500,
                  textAlign: 'left',
                }}>
                  "Receber amigos, familiares ou convidados especiais em casa é um prazer, mas também pode ser uma tarefa desafiadora: lista de compras, organização da mesa, convites, cronograma… Muitas vezes, quem recebe se perde nos detalhes e esquece de aproveitar o momento."
                </Typography>
              </Box>

              {/* Imagem da criadora - Desktop */}
              <Box
                component="img"
                src="/aboutProfilePhoto.png"
                alt="Criadora do Hostly"
                sx={{
                  width: '100%',
                  maxWidth: { sm: '500px', md: '650px', lg: '750px' },
                  height: 'auto',
                  borderRadius: '24px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  border: '3px solid rgba(243, 168, 182, 0.2)',
                  display: 'block',
                  mx: 'auto',
                }}
              />

              {/* Card com nome - Desktop */}
              <Box sx={{
                position: 'absolute',
                bottom: -20,
                left: '15%',
                backgroundColor: 'rgba(243, 168, 182, 0.15)',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(243, 168, 182, 0.3)',
                borderRadius: '24px',
                p: 2.5,
                zIndex: 10,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}>
                <Typography sx={{
                  fontSize: '14px',
                  fontWeight: 300,
                  color: '#F5E8E4',
                  mb: 0.5,
                  fontFamily: '"Inter", "sans-serif"',
                }}>
                  por
                </Typography>
                <Typography sx={{
                  fontSize: '22px',
                  fontWeight: 800,
                  color: '#F5E8E4',
                  textTransform: 'uppercase',
                  fontFamily: '"Montserrat", "sans-serif"',
                  letterSpacing: '1px',
                }}>
                  Rebecca Linhares
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* FOOTER */}
        <Box sx={{
          position: 'relative',
          zIndex: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          py: { xs: 2, md: 2.5 },
          px: { xs: 3, md: 4 },
          mt: { xs: -4, md: -6 },
        }}>
          <Container maxWidth="lg">
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 3 },
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              {/* WhatsApp */}
              <Link
                href="https://wa.me/5561992670863"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#F5E8E4',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: '#F3A8B6',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  WhatsApp
                </Typography>
              </Link>

              {/* Instagram */}
              <Link
                href="https://instagram.com/hostly.app"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#F5E8E4',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: '#F3A8B6',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <InstagramIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  @hostly.app
                </Typography>
              </Link>

              {/* Contact */}
              <Link
                onClick={() => navigate('/contact')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#F5E8E4',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: '#F3A8B6',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <EmailIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  Contato
                </Typography>
              </Link>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '11px', color: '#F5E8E4', opacity: 0.6 }}>
                © {new Date().getFullYear()} Hostly. Todos os direitos reservados.
              </Typography>
            </Box>
          </Container>
        </Box>

      </Box>
    </ThemeProvider>
  );
};

export default App;




